import type { InteractionEvent } from '../../interaction/types';
import type { IViewport } from '../../viewports/types';
import type { ITool } from '../types';
import { FrameCoalescer } from './frame-coalescer';

/**
 * Camera pan tool — drags the camera laterally in its view plane.
 *
 * Reads world-space delta directly from the enriched event payload
 * (event.deltaPoints.world), then shifts both camera position and focal
 * point by that delta. Works correctly for both orthographic and perspective
 * cameras.
 *
 * World coordinates on deltaPoints are recalculated each event with the
 * current camera, so the delta is always correct even after prior pan
 * events moved the camera.
 */
export class PanTool implements ITool {
    static readonly toolName = 'Pan';

    readonly id = PanTool.toolName;
    readonly name = 'Pan';
    readonly cursor = 'grab';
    readonly completed = false;

    private pendingDx = 0;
    private pendingDy = 0;
    private pendingDz = 0;
    private pendingViewport: IViewport | null = null;
    private coalescer = new FrameCoalescer(() => this.applyPending());

    activate(_viewport: IViewport): void {}

    deactivate(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    cancel(): void {
        this.coalescer.cancel();
        this.resetPending();
    }

    handleEvent(event: InteractionEvent): boolean {
        if (event.type === 'mouse_drag_end') {
            this.coalescer.flush();
            return true;
        }

        if (event.type !== 'mouse_drag') return false;

        const [dx, dy, dz] = event.deltaPoints.world;
        this.pendingDx += dx;
        this.pendingDy += dy;
        this.pendingDz += dz;
        this.pendingViewport = event.viewport;
        this.coalescer.schedule();
        return true;
    }

    private applyPending(): void {
        const vp = this.pendingViewport;
        const dx = this.pendingDx;
        const dy = this.pendingDy;
        const dz = this.pendingDz;
        this.resetPending();

        if (!vp || (dx === 0 && dy === 0 && dz === 0)) return;

        const renderer = vp.getRenderer(0);
        const camera = renderer.getActiveCamera();

        // Shift camera in the opposite direction (drag right → camera moves left)
        const pos = camera.getPosition() as [number, number, number];
        const fp = camera.getFocalPoint() as [number, number, number];

        camera.setPosition(pos[0] - dx, pos[1] - dy, pos[2] - dz);
        camera.setFocalPoint(fp[0] - dx, fp[1] - dy, fp[2] - dz);

        vp.updateCameraClippingRange();
        vp.render();
    }

    private resetPending(): void {
        this.pendingDx = 0;
        this.pendingDy = 0;
        this.pendingDz = 0;
        this.pendingViewport = null;
    }
}
